import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import { processAndUploadStrip } from '@renderer/utils/pipeline/outputOrchestrator'
import { isDigitalMode } from '@renderer/lib/runtime/mode'
import type { FrameConfig } from '@renderer/types/configState'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import { BK_HIT, bkHit } from './layout'
import { useBkPhotos } from './useBkPhotos'
import { useBkStickerStore } from './stickers'
import { bakeStickers } from '@renderer/lib/stickers/bakeStickers'
import {
  bkUploadContext,
  startBkPhotoUpload,
  trackBkStripUpload,
  type UserContext
} from './mediaUpload'

/** Safety net only — a print normally lands in well under two seconds. */
const PRINT_TIMEOUT_MS = 15000

/**
 * Screen 9 — "preview", then "print now".
 *
 * ONE `PrintPreview` serves both jobs here: it is what the guest sees and what
 * goes to the printer. Interactive, so a photograph can be dragged and zoomed
 * inside its window exactly as on the stock select page, and the adjustments
 * are already in the canvas that gets exported — there is no second render to
 * keep in step.
 */
const Select: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const sessionId = useSessionStateStore((s) => s.id)
  const gallery = useSessionStateStore((s) => s.gallery)
  const userPreferences = useSessionStateStore((s) => s.userPreferences)
  const { getFrame, config } = useConfigStore()
  const { downloadLocal } = useSettingsStore()
  const { getPrinterForFrame, settings: primarySettings } = usePrintStore()
  const placements = useBkStickerStore((s) => s.placements)
  const photos = useBkPhotos()

  const previewRef = useRef<PrintPreviewRef>(null)
  const slotRef = useRef<HTMLDivElement>(null)
  const [sheetBox, setSheetBox] = useState<{ width: number; height: number } | null>(null)
  const sentRef = useRef(false)
  // A ref, not the state flag: two taps land in the same tick, before React has
  // re-rendered with `busy`, and the second would start a parallel print.
  const busyRef = useRef(false)
  const [busy, setBusy] = useState(false)

  const frameId = userPreferences?.frameId
  const printResolutionScale = primarySettings?.printResolutionScale || 1

  /** The frame at print resolution — `getFrame` scales canvas and slots together. */
  const frameConfig: FrameConfig | null = useMemo(() => {
    if (!frameId) return null
    const scaled = getFrame(frameId, printResolutionScale)
    if (!scaled) return null
    return { id: scaled._id, baseImage: scaled.thumbnail, drawParams: scaled.drawParams }
  }, [frameId, getFrame, printResolutionScale])

  const uploadContext = (): ReturnType<typeof bkUploadContext> =>
    bkUploadContext(context, projectId, downloadLocal)

  /**
   * The sheet's on-screen size: as large as fits the slot, at its own shape.
   *
   * The sticker layer is placed in percentages of this box, so the box must be
   * exactly the drawn sheet. Sizing it with CSS alone (`h-full` + aspect ratio)
   * broke once the 6x8 sheet came in: it is wider than the slot allows at full
   * height, `max-w-full` capped the width but not the height, the canvas drew
   * the sheet centred in a box taller than itself, and stickers placed by % of
   * that taller box floated above and below the sheet.
   */
  const canvasW = frameConfig?.drawParams.canvasWidth ?? 0
  const canvasH = frameConfig?.drawParams.canvasHeight ?? 0
  useEffect(() => {
    const el = slotRef.current
    if (!el || !canvasW || !canvasH) return
    const fit = (): void => {
      const ratio = canvasW / canvasH
      const width = Math.min(el.clientWidth, el.clientHeight * ratio)
      setSheetBox({ width, height: width / ratio })
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return (): void => observer.disconnect()
  }, [canvasW, canvasH])

  const goToGallery = useCallback((): void => {
    uploadSessionState()
    navigate(`/${projectId}/result`)
  }, [navigate, projectId, uploadSessionState])

  const printNow = async (): Promise<void> => {
    if (sentRef.current || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    // Before anything that can fail: the photos belong in the gallery even when
    // the print step does not work out. Capture's confirm normally started this
    // already; asking again is a no-op then.
    void startBkPhotoUpload(uploadContext())

    try {
      if (!previewRef.current || !frameConfig) {
        // `finally` navigates; returning here must not do it a second time.
        logActivity('BK print aborted: no frame config', { level: 'error' })
        cogoToast.error('Frame not ready')
        return
      }

      // Strict first; on a slow asset fall back to rendering whatever is loaded.
      // A degraded strip still beats sending the guest away with nothing.
      let exported = await previewRef.current
        .exportImageWithStatus({ timeoutMs: 8000, strict: true })
        .catch(() => null)
      if (!exported) {
        exported = await previewRef.current.exportImageWithStatus({
          timeoutMs: 5000,
          strict: false
        })
      }

      if (!exported.assetsVerified) {
        logActivity('BK strip exported in degraded mode', {
          degradedReason: exported.degradedReason,
          level: 'warning'
        })
      }

      const stripBlob = await bakeStickers(exported.blob, placements)
      sentRef.current = true

      // Upload, but never let it hold the printer: the guest is standing there.
      // Result holds the QR until it lands (whenBkGalleryReady).
      const ctx = uploadContext()
      const stripUpload = processAndUploadStrip(stripBlob, ctx)
      trackBkStripUpload(ctx.sessionId, stripUpload)
      const uploaded = await Promise.race([
        stripUpload.then((r) => r?.localUrl ?? null),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000))
      ])

      const stripUrl = uploaded ?? URL.createObjectURL(stripBlob)
      setSessionState({
        gallery: {
          stripUrl,
          capturedItems: [
            ...(gallery?.capturedItems ?? []),
            {
              id: `dreambooth-strip-${sessionId}`,
              stripUrl,
              photoUrl: '',
              timestamp: new Date()
            }
          ]
        }
      })

      await sendToPrinter(stripBlob)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      logActivity(`BK print step failed: ${message}`, { level: 'error' })
      cogoToast.error('Print failed')
    } finally {
      busyRef.current = false
      setBusy(false)
      goToGallery()
    }
  }

  // Stock selection timeout proceeds with what is chosen — here, that is
  // printing. Suspended while a print is already under way.
  const remaining = useBkPageTimeout(
    config?.pages?.select?.settings?.selectionTimeout,
    () => void printNow(),
    !busy
  )

  const sendToPrinter = async (stripBlob: Blob): Promise<void> => {
    // Digital booths have no printer at all — skipping here keeps the guest from
    // meeting a print dialog that cannot succeed.
    if (isDigitalMode()) {
      logActivity('BK print skipped: digital mode')
      return
    }

    const printerConfig = getPrinterForFrame(frameId || '')
    if (!printerConfig) {
      logActivity('BK print skipped: no printer configured for this frame', {
        frameId,
        level: 'warning'
      })
      cogoToast.warn('No printer configured')
      return
    }

    const copies = userPreferences?.copies ?? 1
    if (copies < 1) {
      logActivity('BK print skipped: zero copies requested')
      return
    }

    const { printerName, settings } = printerConfig
    logActivity('BK sending print job', {
      printerName,
      copies,
      frameId,
      pageSize: settings.pageSize,
      orientation: settings.orientation
    })

    try {
      await Promise.race([
        initiatePrint(
          stripBlob,
          printerName,
          copies,
          settings,
          navigator.onLine,
          undefined,
          'session',
          {
            sessionId: sessionId || undefined,
            projectId: projectId || undefined
          }
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Print initialization timed out')), PRINT_TIMEOUT_MS)
        )
      ])
      logActivity('BK print job accepted by driver', { printerName, copies })
    } catch (error) {
      // Report rather than swallow: a session that printed nothing must never
      // pass silently, or the operator finds out from the guest.
      const message = error instanceof Error ? error.message : String(error)
      logActivity(`BK print failed: ${message}`, { printerName, copies, level: 'error' })
      cogoToast.error(`Failed to print: ${message}`)
    }
  }

  return (
    <BkScreen plate="preview.webp">
      {/* The strip: seen and printed from the same canvas. */}
      {frameConfig && (
        <div
          ref={slotRef}
          style={bkHit(BK_HIT.select.strip)}
          className="flex items-center justify-center"
        >
          {/* PrintPreview's canvas is `height: 100%` of its container. With no
              height of its own the container fell back to the bitmap's height
              — 2400px for a strip — and the page showed two and a half photos.
              This box is the strip's own shape, sized to the slot's height, so
              the whole strip is always what fits. */}
          <div
            className="relative"
            style={
              sheetBox
                ? { width: sheetBox.width, height: sheetBox.height }
                : { height: '100%', aspectRatio: `${canvasW} / ${canvasH}` }
            }
          >
            <PrintPreview
              ref={previewRef}
              frameConfig={frameConfig}
              selectedPhotos={photos.filter((p): p is string => Boolean(p))}
              sessionId={sessionId || ''}
              isInteractive
              className="h-full w-full"
              includeQrOnStrip={userPreferences?.printGalleryQrOnStrip !== false}
            />
            {/* PrintPreview draws photographs and frame only. Stickers are baked
                onto the exported file at print time (bakeStickers), so the
                preview never showed them and a guest's stickers looked lost.
                They are laid over the canvas here with the exact numbers the
                bake uses — % of the strip for centre and width, height from
                the art's own ratio, rotation about the centre — so this is what
                prints. pointer-events-none keeps drag and zoom on the photos. */}
            {placements.map((sticker) => (
              <img
                key={sticker.id}
                src={sticker.url}
                alt=""
                aria-hidden
                className="pointer-events-none absolute"
                style={{
                  left: `${sticker.xPct}%`,
                  top: `${sticker.yPct}%`,
                  width: `${sticker.widthPct}%`,
                  transform: `translate(-50%, -50%) rotate(${sticker.rotation}deg)`
                }}
              />
            ))}
          </div>
        </div>
      )}

      <button
        aria-label="Print now"
        onClick={printNow}
        disabled={busy}
        style={bkHit(BK_HIT.select.printNow)}
        className="cursor-pointer bg-transparent disabled:cursor-wait"
      />
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Select
